import * as THREE from 'three';
import { gsap } from 'gsap/all';
import each from 'lodash/each';

import Stats from 'stats.js';
import { fragment, vertex } from './shaders';

function GLManager(data) {
    this.totalEntries = data.length;
    this.loadedEntries = 0;

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10000);
    camera.position.z = 8;
    this.camera = camera;
    this.speed = 0.1;

    const scene = new THREE.Scene();
    camera.lookAt = scene.position;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);

    // this.stats = new Stats();
    // this.stats.showPanel(0);
    // document.body.appendChild(this.stats.dom);

    this.render = this.render.bind(this);
    this.factors = data.map((d) => new THREE.Vector2(1, 1));
    this.assets = {};
    this.textures = data.map((entry, i) => {
        const { media } = entry;
        if (!media) {
            return;
        }

        // Video Texture
        if (media.type == 'video') {
            const video = document.createElement('video');
            video.autoplay = true;
            video.loop = true;
            video.muted = true;
            video.playsInline = true;
            const that = this;

            video.onloadedmetadata = function() {
                const texture = new THREE.VideoTexture(video);
                if (navigator.userAgent.indexOf('Firefox') != -1) {
                    texture.format = THREE.RGBAFormat;
                }

                this.onloadedmetadata = null;
                if (i == 0) {
                    this.play();
                }
                that.calculateAspectRatioFactor(i, texture, video);
                return texture;
            };

            video.src = media.url;
            this.assets[i] = {
                media: video,
                canPause: true,
            };
            const texture = new THREE.VideoTexture(video);
            if (navigator.userAgent.indexOf('Firefox') != -1) {
                texture.format = THREE.RGBAFormat;
            }
            // this.calculateAspectRatioFactor(i, texture);
            that.calculateAspectRatioFactor(i, texture, video);
            return texture;
        }

        // Image Texture
        const texture = new THREE.TextureLoader().load(media.url, this.calculateAspectRatioFactor.bind(this, i));
        texture.premultiplyAlpha = true; // Let's make the alpha channel work.
        return texture;
    });

    this.currentIndex = 0;
    this.nextIndex = 0;
    this.textureProgress = 0;
    this.camera = camera;
    this.scene = scene;
    this.renderer = renderer;
    this.initialRender = false;
    this.time = 0;
    this.loopRaf = null;
    this.loop = this.loop.bind(this);
    this.cancelled = false;
}

GLManager.prototype.getViewSize = function() {
    const fovInRadians = (this.camera.fov * Math.PI) / 180;
    const viewSize = Math.abs(
        this.camera.position.z * Math.tan(fovInRadians / 2) * 3,
    );

    return viewSize;
};

GLManager.prototype.getPlaneSize = function() {
    const viewSize = this.getViewSize();
    return { width: viewSize * 1.5, height: viewSize * 1.25 };
};

GLManager.prototype.calculateAspectRatioFactor = function(index, texture, video = null) {
    const plane = this.getPlaneSize();
    const windowRatio = window.innerWidth / window.innerHeight;
    const rectRatio = (plane.width / plane.height) * windowRatio;
    let imageRatio = 2560 / 1600;

    if (video) {
        imageRatio = video.videoWidth / video.videoHeight;
    }

    let factorX = 1;
    let factorY = 1;
    if (rectRatio > imageRatio) {
        factorX = 1;
        factorY = (1 / rectRatio) * imageRatio;
    } else {
        factorX = (1 * rectRatio) / imageRatio;
        factorY = 1;
    }

    this.factors[index] = new THREE.Vector2(factorX, factorY);
    if (this.currentIndex === index) {
        this.mesh.material.uniforms.u_textureFactor.value = this.factors[index];
        this.mesh.material.uniforms.u_textureFactor.needsUpdate = true;
    }
    if (this.nextIndex === index) {
        this.mesh.material.uniforms.u_texture2Factor.value = this.factors[index];
        this.mesh.material.uniforms.u_texture2Factor.needsUpdate = true;
    }
    if (this.initialRender) {
        this.loadedEntries++;
        if (this.loadedEntries === this.totalEntries) {
            document.body.classList.remove('loading');
        }
        // this.render();
    }
};
// Plane Stuff
GLManager.prototype.createPlane = function() {
    // Calculate bas of Isoceles triangle(camera)
    const viewSize = this.getViewSize();
    const { width, height } = this.getPlaneSize();

    const segments = 60;
    const geometry = new THREE.PlaneBufferGeometry(
        width / 2,
        height / 2,
        segments,
        segments,
    );

    const material = new THREE.ShaderMaterial({
        uniforms: {
            u_texture: { type: 't', value: this.textures[this.currentIndex] },
            u_textureFactor: { type: 'f', value: this.factors[this.currentIndex] },
            u_texture2: { type: 't', value: this.textures[this.nextIndex] },
            u_texture2Factor: { type: 'f', value: this.factors[this.nextIndex] },
            u_textureProgress: { type: 'f', value: this.textureProgress },
            u_offset: { type: 'f', value: 8 },
            u_progress: { type: 'f', value: 0 },
            u_direction: { type: 'f', value: 1 },
            u_effect: { type: 'f', value: 0 },
            u_time: { type: 'f', value: this.time },
            u_waveIntensity: { type: 'f', value: 0 },
            intensity: {
                value: 0.3, type: 'f', min: 0.0, max: 2,
            },
            u_resolution: {
                type: 'v2',
                value: new THREE.Vector2(window.innerWidth, window.innerHeight),
            },
            u_rgbPosition: {
                type: 'v2',
                value: new THREE.Vector2(window.innerWidth / 2, window.innerHeight / 2),
            },
            u_rgbVelocity: { type: 'v2', value: new THREE.Vector2(0, 0) },
        },
        vertexShader: vertex,
        fragmentShader: fragment,
        side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    this.scene.add(mesh);
    // this.scene.add( mesh1);
    this.mesh = mesh;
};

GLManager.prototype.updateTexture = function(newIndex, progress) {
    let didChange = false;
    if (newIndex != null && this.newIndex !== this.currentIndex) {
        this.currentIndex = this.nextIndex;
        this.nextIndex = newIndex;
        this.textureProgress = 0;
        this.mesh.material.uniforms.u_textureProgress.value = 0;
        this.mesh.material.uniforms.u_texture.value = this.textures[
        this.currentIndex
        ];
        this.mesh.material.uniforms.u_textureFactor.value = this.factors[
        this.currentIndex
        ];
        this.mesh.material.uniforms.u_texture2.value = this.textures[newIndex];

        this.mesh.material.uniforms.u_texture2Factor.value = this.factors[newIndex];

        didChange = true;

        // Play Video if needed:
        each(this.assets, (object, index) => {
            if (object.canPause) {
                object.media.pause();
                object.media.currentTime = 0;
            }
        });

        if (this.assets[newIndex]) {
            const video = this.assets[newIndex];
            video.canPause = false;
            const playPromise = video.media.play();
            if (playPromise !== undefined) {
                playPromise.then((_) => {
                    // Automatic playback started!
                    // Show playing UI.
                    // We can now safely pause video...
                    video.canPause = true;
                })
                .catch((error) => {
                    // Auto-play was prevented
                    // Show paused UI.
                    video.canPause = true;
                });
            }
        }
    }

    if (progress != null && progress !== this.textureProgress) {
        this.mesh.material.uniforms.u_textureProgress.value = progress;
        this.textureProgress = progress;
        didChange = true;
    }

    if (!this.loopRaf && didChange) {
        this.render();
    }
};

GLManager.prototype.updateStickEffect = function({
    progress,
    direction,
    waveIntensity,
}) {
    this.mesh.material.uniforms.u_progress.value = progress;
    this.mesh.material.uniforms.u_direction.value = direction;
    this.mesh.material.uniforms.u_waveIntensity.value = waveIntensity;
};

GLManager.prototype.updateRgbEffect = function({ position, velocity }) {
    this.mesh.material.uniforms.u_rgbPosition.value = new THREE.Vector2(
        position.x,
        position.y,
    );
    this.mesh.material.uniforms.u_rgbVelocity.value = new THREE.Vector2(
        velocity.x,
        velocity.y,
    );

    if (!this.loopRaf) {
        this.render();
    }
};
// Other stuff
GLManager.prototype.render = function() {
    if (!this.initialRender) {
        this.initialRender = true;
    }

    // if (!this.gl) {
    // this.gl = document.querySelector('#slider canvas').getContext("webgl");
    // }
    // this.renderer.clear(); // Stay ahead of garbage collection.
    // this.gl.readPixels(0, 0, 1, 1, this.gl.RGBA, this.gl.UNSIGNED_BYTE, new Uint8Array(4));
    this.renderer.render(this.scene, this.camera);
};

GLManager.prototype.mount = function(container) {
    this.renderer.domElement.style.visibility = 'hidden';
    this.renderer.domElement.style.opacity = 0;
    container.prepend(this.renderer.domElement);
};

GLManager.prototype.unmount = function() {
    this.textures.forEach((texture) => {
        texture.dispose();
    });

    this.mesh.material.dispose();
    this.mesh.geometry.dispose();
    this.mesh = null;
    this.renderer = null;
    this.camera = null;
    this.scene = null;
    this.container = null;
};

GLManager.prototype.onResize = function() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.mesh.material.uniforms.u_resolution.value = new THREE.Vector2(
        window.innerWidth,
        window.innerHeight,
    );
    // this.camera.aspect = window.inenrWidth / window.innerHeight;
    // this.camera.updateProjectionMatrix();
    for (let i = 0; i < this.textures.length; i++) {
        if (this.textures[i].image) {
            this.calculateAspectRatioFactor(i, this.textures[i]);
        }
    }

    // this.render();
};

GLManager.prototype.scheduleLoop = function() {
    if (this.loopRaf) {
        return;
    }

    this.loopRaf = false;
    gsap.ticker.add(this.loop);
    // Manual loop.
    // this.loop();
};

/**
 * Loops the render function.
 */
GLManager.prototype.loop = function() {
    this.render();
    this.time += this.speed;
    this.mesh.material.uniforms.u_time.value = this.time;

    // Manual loop.
    // this.loopRaf = requestAnimationFrame(this.loop);
};

/**
 * Cancel the animation loop.
 */
GLManager.prototype.cancelLoop = function() {
    gsap.ticker.remove(this.loop);
    this.cancelled = true;
    this.loopRaf = false;
};

export { GLManager };
